// The confirmation screen after a successful booking. Shows the booked local
// time (in the visitor's timezone) and a manage link to cancel or reschedule.
// Implements: architecture.md v1.0 section 5 (manage link carries signed token).

import { useState } from 'react';
import type { JSX } from 'react';
import type { PublicBooking } from '../api/types.js';
import { formatLongDate, formatTimeRange, shiftInstant, zoneLabel } from '../lib/time.js';
import { bookingPathLink, manageAbsoluteLink, managePathLink, navigate } from '../lib/router.js';

export function Confirmation({
  booking,
  manageToken,
  slug,
  tz,
  typeName,
  frontBufferMin = 0,
}: {
  booking: PublicBooking;
  manageToken: string;
  /** Host slug, so the manage link stays scoped to this host (ADR-0002 §5). */
  slug: string;
  tz: string;
  typeName: string;
  /**
   * The selected booking type's front buffer, in minutes (0 when absent). The
   * confirmation shows the ACTUAL meeting start (booking.start + frontBufferMin),
   * consistent with the calendar invite the write-target event carries. Display
   * only: booking.start itself (the server record) stays nominal.
   */
  frontBufferMin?: number;
}): JSX.Element {
  // The shifted actual meeting start; the end stays the nominal end, so a
  // 13:30 slot with a 5-minute front buffer reads 13:35 to 14:00.
  const displayStart = shiftInstant(booking.start, frontBufferMin);
  const [copied, setCopied] = useState(false);

  async function copyLink(): Promise<void> {
    const link = manageAbsoluteLink(slug, manageToken);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable (insecure context); ignore silently.
    }
  }

  return (
    <div className="confirm rise">
      <div className="confirm-mark" aria-hidden="true">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M5 13l4 4L19 7" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <span className="eyebrow">Booked</span>
      <h2>You are all set</h2>
      <p className="page-desc" style={{ margin: '0.5rem auto 0' }}>
        We have sent a calendar invite to <strong>{booking.customer.email}</strong>. A confirmation
        for <strong>{typeName}</strong> is below.
      </p>

      <div className="confirm-when">
        <div className="cw-time">{formatTimeRange(displayStart, booking.end, tz)}</div>
        <div className="cw-date">{formatLongDate(displayStart, tz)}</div>
        <div className="cw-tz">{zoneLabel(tz, displayStart)}</div>
      </div>

      <div className="manage-row">
        <a className="btn btn-ghost" href={managePathLink(slug, manageToken)}>
          Manage this booking
        </a>
        <button type="button" className="btn-link" onClick={copyLink}>
          {copied ? 'Link copied' : 'Copy manage link'}
        </button>
      </div>

      <p className="foot-note">
        Need to make a change? Use the manage link to reschedule or cancel any time.{' '}
        <button
          type="button"
          className="btn-link"
          onClick={() => navigate(bookingPathLink(slug))}
        >
          Book another
        </button>
      </p>
    </div>
  );
}
